import React from "react";
import { motion } from "framer-motion";
import {
  LineChart,
  Target,
  Users,
  BadgeDollarSign,
  Lightbulb,
  Clock,
  Headphones,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Reason = {
  title: string;
  description: string;
  icon: LucideIcon;
};

const reasons: Reason[] = [
  { title: "Results-Driven", description: "Every campaign is built around measurable KPIs and ROI targets.", icon: LineChart },
  { title: "Targeted Strategies", description: "Reach the right audience with precision targeting and segmentation.", icon: Target },
  { title: "Expert Team", description: "Certified specialists in SEO, PPC, social media, and content marketing.", icon: Users },
  { title: "Transparent Pricing", description: "Clear packages with no hidden fees — you always know what you're paying for.", icon: BadgeDollarSign },
  { title: "Creative Campaigns", description: "Stand out with compelling ad copy, visuals, and brand storytelling.", icon: Lightbulb },
  { title: "Fast Turnaround", description: "Campaigns go live quickly with agile planning and rapid execution.", icon: Clock },
  { title: "Dedicated Support", description: "A single point of contact for strategy, reporting, and optimization.", icon: Headphones },
  { title: "Proven Track Record", description: "Trusted by 150+ businesses across industries for consistent growth.", icon: Trophy },
];

const DmWhyChoose: React.FC = () => (
  <AnimatedSection as="section" className="dm-section dm-section--alt" aria-labelledby="dm-why-title">
    <div className="dm-container">
      <SectionHeader
        eyebrow="Why Choose WebTech"
        title="Your Trusted Digital Marketing Partner"
        text="We combine creative strategy with data analytics to deliver marketing that actually converts."
      />

      <div className="dm-why-grid">
        {reasons.map((reason, index) => {
          const Icon = reason.icon;
          return (
            <motion.article
              key={reason.title}
              className="dm-why-card"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.05 }}
              whileHover={{ y: -6 }}
            >
              <div className="dm-why-card__icon">
                <Icon size={22} aria-hidden="true" />
              </div>
              <h3>{reason.title}</h3>
              <p>{reason.description}</p>
            </motion.article>
          );
        })}
      </div>
    </div>
  </AnimatedSection>
);

export default DmWhyChoose;
